<template>
  <div class="d-flex gap-1 align-center">
    <SoIcon icon="info" color="info" />
    The values displayed on the filters represent only open complaints
  </div>
  <SoRadioCards
    v-model="topLevelFilters"
    class="mt-2 mb-6"
    :mandatory="false"
    :multiple="true"
  >
    <v-row
      v-if="complaintColumnValues.isFetched && complaintsSummary.isFetched"
    >
      <v-col
        v-for="(filter, index) in topFiltersOptions"
        :key="index"
        cols="12"
        sm="6"
        lg="3"
      >
        <SoRadioCardChart
          v-if="filter"
          :title="filter.label"
          :value="filter.name"
          :percentage="filter.percentage"
          :type="filter.type"
          :is-loading="
            complaintsSummary.isFetching && complaintsSummary.isFetching
          "
        >
          <span v-if="filter.name === 'open'" class="text-h2">
            {{ filter.count }}
          </span>
          <span v-else class="text-h2">
            {{ filter.count }} ({{ filter.percentage }}%)
          </span>
        </SoRadioCardChart>
      </v-col>
    </v-row>
  </SoRadioCards>
  <SoDialog
    v-model="isColumnSelectorShown"
    card-title="Customise columns"
    :loading="false"
    :max-width="1400"
    data-test-id="customize-columns-modal"
  >
    <OpsComplaintColumnSelector
      v-model="selectedHeaders"
      :config="columnSelectorConfig"
    />
    <div class="d-flex gap-1 justify-end">
      <SoButton
        class="gap-1"
        title="Default view"
        data-test-id="customize-columns-default-view-btn-id"
        large
        @click="resetHeaders()"
      />
      <SoButton
        title="Apply changes"
        type="success"
        large
        data-test-id="customize-columns-apply-change-btn-id"
        @click="updateHeaders()"
      />
    </div>
  </SoDialog>

  <SoAlert v-if="query?.error" title="Failed to render complaints table" />
  <SoDataTableServer
    v-else
    v-model:options="options"
    :headers="headers"
    :data="tableData"
    :are-filters-loading="
      complaintColumnValues.isFetching && complaintsSummary.isFetching
    "
    select-key="id"
    card
    @fetch="fetchComplaints"
  >
    <template #tableActions>
      <SoButton
        data-test-id="customize-columns-btn-id"
        title="Customize"
        :large="true"
        prepend-icon="table-edit"
        @click="isColumnSelectorShown = true"
      />
      <SoExportCsvButton
        :data="resultsCSVConfig.data"
        :labels="resultsCSVConfig.headers"
        :file-name="resultsCSVConfig.fileName()"
        @export="exportCSV()"
      />
      <v-spacer />

      <OpsComplaintTableSummary :filters="appliedTableFilters" />
    </template>
  </SoDataTableServer>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { QueryResult } from "@/api/utils/types"
  import { useSnackbarStore } from "@/stores/snackbar"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDataTableServer, {
    SoDataTableData,
    SoDataTableOptions,
  } from "@/components/dataTable/SoDataTableServer.vue"
  import {
    allHeaders,
    columnSelectorConfig,
    ComplaintFilterTypes,
    defaultHeaderKeys,
    prepareOptionsForFilter,
    TableFilters,
    tableFilters,
    TopFilter,
    topFilterOptions,
    topFiltersToTableFilters,
  } from "../opsComplaintConfig"
  import {
    ComplaintQueryResult,
    ComplaintService,
  } from "@/services/operations/ComplaintService"
  import rules from "@/validators/rules"
  import SoDialog from "@/components/SoDialog.vue"
  import OpsComplaintColumnSelector from "@/views/operations/complaints/components/OpsComplaintColumnSelector.vue"
  import { safeDayjs } from "@/helpers/dateHelper"
  import { IComplaintColumn } from "@/models/operations/complaintColumn/complaintColumn.types"
  import SoRadioCards from "@/components/SoRadioCards.vue"
  import SoRadioCardChart from "@/components/SoRadioCardChart.vue"
  import { IComplaintSummary } from "@/models/operations/complaintSummary/complaintSummary.types"
  import { FilterType, SoDataTableFilter } from "@/types/components/SoDataTable"
  import { findChangesBetween } from "@/helpers/arrayHelpers"
  import SoExportCsvButton from "@/components/dataTable/SoExportCsvButton.vue"
  import { ComplaintCSVExporter } from "@/models/complaint/complaintCSVExporter"
  import SoIcon from "@/components/SoIcon.vue"
  import OpsComplaintTableSummary from "@/views/operations/complaints/components/OpsComplaintTableSummary.vue"

  const MAX_DOWNLOAD_LIMIT = 2_000_000

  export default defineComponent({
    components: {
      OpsComplaintTableSummary,
      SoIcon,
      SoExportCsvButton,
      SoRadioCardChart,
      SoRadioCards,
      OpsComplaintColumnSelector,
      SoDialog,
      SoDataTableServer,
      SoAlert,
      SoButton,
    },
    data() {
      return {
        columnSelectorConfig,
        query: null as QueryResult<ComplaintQueryResult> | null,
        allHeaders,
        options: {
          filters: tableFilters,
        } as Partial<SoDataTableOptions>,
        snackbarStore: useSnackbarStore(),
        complaintColumnValues: ComplaintService.getUniqueColumnValues(),
        complaintsSummary: ComplaintService.getComplaintsSummary(),
        rules,
        prevTopFilters: [] as (keyof IComplaintSummary)[],
        isColumnSelectorShown: false,
        selectedHeaders: defaultHeaderKeys,
        headers: allHeaders.filter((h) => defaultHeaderKeys.includes(h.key)),
        exportQuery: null as QueryResult<ComplaintQueryResult> | null,
      }
    },
    computed: {
      appliedTableFilters() {
        return this.mapFilters(this.options.filters as TableFilters)
      },
      resultsCSVConfig() {
        const exporter = new ComplaintCSVExporter(this.exportQuery)
        return exporter.getConfig()
      },
      topFiltersOptions(): (TopFilter | null)[] {
        const total = this.complaintsSummary?.data?.open ?? 0
        return topFilterOptions.map((filter) => {
          if (!filter) {
            return null
          }
          const count = this.complaintsSummary?.data?.[filter.name] ?? 0
          const percentage = count === 0 ? 0 : Math.round((count / total) * 100)
          return {
            ...filter,
            count,
            percentage,
          }
        })
      },
      complaintColumnFilters() {
        return this.complaintColumnValues.data
      },
      tableData(): SoDataTableData {
        return {
          isLoading: !!this.query?.isFetching,
          totalItems: this.query?.data?.totalCount ?? 0,
          items: this.query?.data?.records ?? [],
        }
      },
      /**
       * Handles link between top level filters (pie charts) and table filters from `SoRadioCards`
       */
      topLevelFilters: {
        get(): (keyof IComplaintSummary)[] {
          const filters = this.options.filters
          if (!filters) {
            return []
          }
          const highLevelFilters: (keyof IComplaintSummary)[] = []

          Object.entries(topFiltersToTableFilters).forEach(
            ([topFilter, { filterPath, filterValues }]) => {
              const appliedFilters = filters[filterPath]?.value as
                string[] | undefined

              if (filterValues.every((f) => appliedFilters?.includes(f))) {
                highLevelFilters.push(topFilter as keyof IComplaintSummary)
              }
            },
          )

          return highLevelFilters
        },
        /**
         * Updates table filters based on difference between previous top filters and current top filters
         * @param value
         */
        set(value: (keyof IComplaintSummary)[]) {
          const { added, removed } = findChangesBetween(
            value,
            this.prevTopFilters,
          )

          const tableFilters = this.options.filters

          if (!tableFilters) {
            return
          }

          added.forEach((topFilter) => {
            const name = topFiltersToTableFilters[topFilter].filterPath
            const previousTableFilters = tableFilters[name].value

            tableFilters[name].value = [
              ...new Set([
                ...((previousTableFilters || []) as string[]),
                ...topFiltersToTableFilters[topFilter].filterValues,
              ]),
            ]
          })

          removed.forEach((topFilter) => {
            const tableFilter: SoDataTableFilter =
              tableFilters[topFiltersToTableFilters[topFilter].filterPath]

            if (tableFilter.type !== FilterType.CHECKBOX) {
              return
            }

            const isTableFilterLinkedToTopFilter = (filter: string) =>
              !topFiltersToTableFilters[topFilter].filterValues.includes(filter)

            tableFilter.value = tableFilter.value.filter(
              isTableFilterLinkedToTopFilter,
            )
          })
        },
      },
    },
    watch: {
      topLevelFilters(value: (keyof IComplaintSummary)[]) {
        this.prevTopFilters = value
      },
      complaintColumnFilters(value?: IComplaintColumn) {
        this.updateColumnFilters(value)
      },
      isColumnSelectorShown(value: boolean) {
        if (!value) {
          setTimeout(() => {
            this.selectedHeaders = this.headers.map((h) => h.key)
          }, 100)
        }
      },
    },
    mounted() {
      if (this.complaintColumnValues.isFetched) {
        this.updateColumnFilters(this.complaintColumnValues.data)
      }
    },
    methods: {
      updateColumnFilters(data?: IComplaintColumn) {
        if (!data) return

        Object.keys(data).forEach((filterName) => {
          const filter = this.options?.filters?.[filterName]
          const options = data[filterName as keyof IComplaintColumn]
          const hasOptions = filter && "options" in filter && options

          if (hasOptions) {
            filter.options = prepareOptionsForFilter(
              filterName as keyof IComplaintColumn,
              options,
            )
          }
        })
      },
      updateHeaders() {
        this.headers = allHeaders.filter((header) =>
          this.selectedHeaders.includes(header.key),
        )
        this.isColumnSelectorShown = false
      },
      mapFilters(filters: TableFilters): ComplaintFilterTypes {
        const mapArrayFilter = (filterValue: string[]) =>
          filterValue?.length ? filterValue : undefined

        const mapDateToFilter = (date: Date | null | undefined) =>
          date ? safeDayjs(date)?.add(1, "day") : undefined

        return {
          dateRaisedFrom: safeDayjs(filters.dateRaised.value.from),
          dateRaisedTo: mapDateToFilter(filters.dateRaised.value.to),
          status: mapArrayFilter(filters.status.value),
          channel: mapArrayFilter(filters.channel.value),
          agentName: mapArrayFilter(filters.agentName.value),
          team: mapArrayFilter(filters.team.value),
          type: mapArrayFilter(filters.type.value),
          subStatus: mapArrayFilter(filters.subStatus.value),
          category1: mapArrayFilter(filters.category1.value),
          category2: mapArrayFilter(filters.category2.value),
          category3: mapArrayFilter(filters.category3.value),
          internalCategory: mapArrayFilter(filters.internalCategory.value),
          ofgemCategory: mapArrayFilter(filters.ofgemCategory.value),
          ageProfile: mapArrayFilter(filters.ageProfile.value),
        }
      },
      resetHeaders() {
        this.selectedHeaders = defaultHeaderKeys
      },
      fetchComplaints({
        page,
        pageSize,
        orderBy,
        filters,
      }: SoDataTableOptions) {
        if (orderBy && Object.keys(orderBy).length === 0 && !this.query) {
          orderBy["date_raised"] = "desc"
        }

        this.query = ComplaintService.getComplaintList(
          page,
          pageSize,
          orderBy,
          this.mapFilters(filters as TableFilters),
        )
      },
      exportCSV() {
        const { orderBy, filters } = this.options as SoDataTableOptions
        if (orderBy && Object.keys(orderBy).length === 0 && !this.query) {
          orderBy["date_raised"] = "desc"
        }

        this.exportQuery = ComplaintService.getComplaintList(
          1,
          MAX_DOWNLOAD_LIMIT,
          orderBy,
          this.mapFilters(filters as TableFilters),
        )
      },
    },
  })
</script>
